import React from "react";
import { Helmet } from "react-helmet-async";

export default function DtfUnderbaseMisalignment() {
  return (
    <>
      <Helmet>
        <title>DTF Underbase Misalignment? Why White Ink Is Not Lining Up</title>
        <meta
          name="description"
          content="DTF underbase misalignment causing white ink outlines, shadows, or offset layers? Learn the most common causes so you can fix the real alignment problem."
        />
      </Helmet>

      <div className="px-4 py-10 max-w-3xl mx-auto text-white">
        <h1 className="text-3xl font-bold mb-6 text-center">
          DTF Underbase Misalignment? Here’s Why Your White Ink Is Off
        </h1>

        <p className="mb-6 text-lg text-gray-300">
          If your white underbase is not lining up with your color print, you
          may see outlines, shadows, offset edges, or a sloppy finished image.
          This is known as underbase misalignment, and it can make good artwork
          look cheap fast.
        </p>

        <p className="mb-6 text-gray-300">
          The reason this problem is so frustrating is that it can come from
          both mechanical and software causes. Alignment, calibration, carriage
          movement, film movement, and RIP output can all play a role, which
          makes it easy to adjust the wrong thing.
        </p>

        <div className="mb-8 p-5 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            Misalignment Usually Means the Process Is Drifting Somewhere
          </h2>
          <p className="text-gray-300 mb-4">
            DTF Wizard helps identify whether your alignment issue is coming
            from calibration, printer behavior, output setup, or film movement
            so you can fix the actual source instead of chasing symptoms.
          </p>
          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Common Causes of DTF Underbase Misalignment
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>Alignment or calibration inconsistencies</li>
          <li>Bi-directional print behavior issues</li>
          <li>Carriage or movement-related variation</li>
          <li>RIP or output setup inconsistencies</li>
          <li>Film movement or stability problems during printing</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          What This Usually Looks Like
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>White ink outline showing around the color layer</li>
          <li>Offset edges in one direction</li>
          <li>Shadowed appearance around the artwork</li>
          <li>Misalignment changes across the width of the print</li>
          <li>Some files look worse than others even on the same machine</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Quick Things to Check First
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>See whether the offset stays consistent in one direction</li>
          <li>Compare whether the issue affects all prints or only some designs</li>
          <li>Watch whether the misalignment changes across the film width</li>
          <li>Look for patterns that suggest movement or alignment drift</li>
          <li>Pay attention to whether the issue seems mechanical or output-related</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Why This Is Hard to Fix
        </h2>
        <p className="text-gray-300">
          Underbase misalignment is hard to solve when you do not know where the
          drift begins. If the root cause is mechanical but you only adjust RIP
          settings, the issue stays. If the cause is output-related but you keep
          chasing printer movement, you lose time without fixing it.
        </p>

        <div className="mt-10 p-6 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            Want the Exact Fix for Your Setup?
          </h2>

          <p className="text-gray-300 mb-4">
            DTF Wizard helps you break down whether your white underbase
            misalignment is caused by calibration, print direction, film
            movement, or output setup so you can fix the correct source.
          </p>

          <p className="text-gray-400 mb-4 italic">
            Fix one issue and this already pays for itself.
          </p>

          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-semibold mb-4">FAQ</h2>

          <div className="space-y-4 text-gray-300">
            <p>
              <strong>Why is my white ink not lining up with my color?</strong>
              <br />
              That usually points to an alignment, movement, or output
              consistency issue somewhere in the print process.
            </p>

            <p>
              <strong>Why is the offset always in the same direction?</strong>
              <br />
              A consistent directional shift often suggests calibration or
              directional alignment is off.
            </p>

            <p>
              <strong>Can this be caused by software settings?</strong>
              <br />
              Yes. RIP configuration and output setup can affect how the white
              and color layers align during printing.
            </p>
          </div>
        </div>

        <div className="mt-12 text-gray-400">
          <p className="mb-2 font-semibold">Related DTF Issues:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <a
                href="/fix/dtf-ghosting"
                className="text-blue-400 hover:underline"
              >
                DTF Ghosting
              </a>
            </li>
            <li>
              <a
                href="/fix/dtf-banding-lines"
                className="text-blue-400 hover:underline"
              >
                DTF Banding Lines
              </a>
            </li>
            <li>
              <a
                href="/fix/dtf-colors-dull"
                className="text-blue-400 hover:underline"
              >
                DTF Colors Dull or Faded
              </a>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}